'use client';

import { useEffect, useRef } from 'react';
import { cx } from '@/lib/utils';

interface PinFieldProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  loading?: boolean;
  disabled?: boolean;
  containerClassName?: string;
  inputClassName?: string;
}

const PinField: React.FC<PinFieldProps> = ({
  length = 4,
  value,
  onChange,
  autoFocus = true,
  loading = false,
  disabled,
  containerClassName,
  inputClassName,
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!value) {
      inputRefs.current[0].focus();
    }
  }, [value]);

  useEffect(() => {
    if (!loading && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [loading]);

  const handleChange = (val: string, index: number) => {
    const newChar = val.slice(-1); // keep last char
    const valueArr = value.split('');

    // Replace the character at the current index
    valueArr[index] = newChar;

    const newVal = valueArr.join('');
    onChange(newVal);

    if (newChar && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('Text').trim();
    if (!pasteData) return;

    const chars = pasteData.split('').slice(0, length);
    const valueArr = value.split('');

    // Insert pasted chars from the start
    for (let i = 0; i < chars.length; i++) {
      valueArr[i] = chars[i];
    }

    const newVal = valueArr.join('');
    onChange(newVal);

    const lastFilledIndex = Math.min(chars.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  return (
    <div
      className={cx(
        'flex items-center gap-4 sm:gap-6 sm:rtl:justify-end',
        containerClassName,
      )}
      dir="ltr"
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            if (el) inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={cx(
            'h-14 w-12 rounded-xl border-none text-center text-[18px] font-medium leading-10 text-gray-700 focus:ring-2 focus:ring-blue-300 sm:h-16 sm:w-14',
            loading
              ? 'ring-none bg-gray-100 !text-gray-400'
              : 'bg-transparent shadow-sm ring-[0.7px] ring-gray-300',
            inputClassName,
          )}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={loading || disabled}
        />
      ))}
    </div>
  );
};

export default PinField;
